import React, { useMemo, useState } from "react";
import { Layout, Menu, Badge, Space } from "antd";
import type { MenuProps } from "antd";
import {
    BarChartOutlined,
    HomeOutlined,
    SettingOutlined,
    ShoppingOutlined,
    UnorderedListOutlined,
} from "@ant-design/icons";

const { Sider } = Layout;

export type SideBarItem = {
    key: string;
    label: React.ReactNode;
    icon?: React.ReactNode;
    to?: string;
    onClick?: () => void;
    badge?: number | React.ReactNode;
    disabled?: boolean;
    children?: SideBarItem[];
};

export type SideBarProps = {
    open?: boolean;
    selectedKey?: string;
    onOpenChange?: (open: boolean) => void;
    onSelect?: (key: string, item: SideBarItem) => void;
    onNavigate?: (to: string, item: SideBarItem) => void;
};

const SIDE_BAR_ITEMS: SideBarItem[] = [
    { key: "home", label: "Главная", icon: <HomeOutlined />, to: "/" },
    { key: "products", label: "Товары", icon: <ShoppingOutlined />, to: "/products" },
    {
        key: "activity-panel",
        label: "Панель Активности",
        icon: <UnorderedListOutlined />,
        to: "/activity-panel",
    },
    {
        key: "analytics",
        label: "Аналитика",
        icon: <BarChartOutlined />,
        children: [{ key: "dash", label: "Дашборды", to: "/dashboards" }],
    },
    {
        key: "settings",
        label: "Настройки",
        icon: <SettingOutlined />,
        to: "/settings",
    },
];

function flatten(items: SideBarItem[]): SideBarItem[] {
    const out: SideBarItem[] = [];
    const walk = (arr: SideBarItem[]) => {
        for (const it of arr) {
            out.push(it);
            if (it.children) walk(it.children);
        }
    };
    walk(items);
    return out;
}

export default function SideBar({
    open,
    selectedKey,
    onOpenChange,
    onSelect,
    onNavigate,
}: SideBarProps) {
    const [innerOpen, setInnerOpen] = useState<boolean>(true);
    const visible = typeof open === "boolean" ? open : innerOpen;
    const setVisible = (v: boolean) => {
        if (typeof open !== "boolean") setInnerOpen(v);
        onOpenChange?.(v);
    };

    // состояние "ниже брейкпоинта" от antd
    const [broken, setBroken] = useState(false);

    const menuItems: MenuProps["items"] = useMemo(() => {
        const map = (arr: SideBarItem[]): Required<MenuProps>["items"] =>
            arr.map((it) => {
                const labelNode =
                    it.badge !== undefined ? (
                        <Space>
                            <span>{it.label}</span>
                            <Badge size="small" count={it.badge} />
                        </Space>
                    ) : (
                        it.label
                    );
                return it.children?.length
                    ? {
                          key: it.key,
                          label: labelNode,
                          icon: it.icon,
                          children: map(it.children),
                      }
                    : {
                          key: it.key,
                          label: labelNode,
                          icon: it.icon,
                          disabled: it.disabled,
                      };
            });
        return map(SIDE_BAR_ITEMS);
    }, [SIDE_BAR_ITEMS]);

    const flat = useMemo(() => flatten(SIDE_BAR_ITEMS), [SIDE_BAR_ITEMS]);

    const handleClick: MenuProps["onClick"] = (info) => {
        const item = flat.find((i) => i.key === info.key);
        if (!item) return;
        onSelect?.(info.key as string, item);
        if (item.onClick) item.onClick();
        if (item.to && onNavigate) onNavigate(item.to, item);
        // На мобильных удобно автозакрывать
        if (broken) setVisible(false);
    };

    return (
        <>
            {/* overlay: тень на контенте */}
            {/* {
                <div
                    aria-hidden
                    onClick={() => setVisible(false)}
                    className={cx(
                        "fixed inset-0 z-10",
                        "bg-black/40 backdrop-blur-[1px]",
                        "transition-opacity duration-200",
                        visible ? "opacity-100" : "opacity-0 pointer-events-none",
                    )}
                />
            } */}

            <Sider
                collapsed={!visible}
                onCollapse={(c) => setVisible(!c)}
                collapsedWidth="0"
                width={220}
                breakpoint={"xl"}
                onBreakpoint={(b) => setBroken(b)}
                className={`sticky overflow-hidden border-l h-screen start-0 top-0 bottom-0 left-0 w-40 z-[9]`}
                style={{ scrollbarWidth: "thin", scrollbarGutter: "stable" }}
            >
                {/* Меню */}
                {/* <div className="h-full"> */}
                <Menu
                    className="h-full"
                    mode="inline"
                    items={menuItems}
                    onClick={handleClick}
                    defaultOpenKeys={["home"]}
                    selectedKeys={selectedKey ? [selectedKey] : undefined}
                    // className="!border-0"
                />
                {/* </div> */}
            </Sider>
        </>
    );
}
